import type { CreateInvoiceParams, TmsInvoiceRow, TmsWriteRow } from '@numeo/types-main-web-app'
import { approveWrite, type WriteOutcome } from '../../../api/writeCardApi'
import { refreshInvoicingRow } from './refreshRow'

/** What one step of the drawer's flow came to: done (with the row as it now reads, when it could be read), or said to the user. */
export type StepResult = { ok: true; row?: TmsInvoiceRow } | { ok: false; message: string; row?: TmsInvoiceRow }

const FALLBACK = 'The card did not complete this step.'

/** The row of an answer that did not go through: its refusal in one plain sentence, else the card's own text. Null when it went through. */
const refusalOf = (outcome: WriteOutcome): string | null => {
    if (outcome.status === 'question') return outcome.question?.message || FALLBACK
    if (outcome.status === 'refused') return outcome.text || FALLBACK
    const refused: TmsWriteRow | undefined = outcome.structured.rows?.find((row) => row.outcome === 'refused' || row.outcome === 'skipped')
    if (!refused) return null
    return refused.refusal_title || refused.message || outcome.text || FALLBACK
}

/** Reads the load's row again after a step, so the drawer and the table show what the card now says. */
const after = async (loadNumber: string): Promise<TmsInvoiceRow | undefined> => refreshInvoicingRow(loadNumber)

/**
 * Confirms the POD on the load (`tms_load_documents.accept`). The button is the approval: an accept that asks (findings) is answered
 * at once, under the question's own state. The drawer stays open; its row is read again.
 */
export async function confirmDocumentMatch(tabId: string, loadId: string, loadNumber: string, reason: string): Promise<StepResult> {
    const outcome = await approveWrite(
        tabId,
        'tms_load_documents.accept',
        { load_id: loadId, document: 'pod', reason: reason.trim() },
        { approvedInDrawer: true }
    )
    const refusal = refusalOf(outcome)
    if (refusal) return { ok: false, message: refusal }
    return { ok: true, row: await after(loadNumber) }
}

/** Saves the load's Original Rate (`tms_loads.edit`) so its invoice has a line haul. A question the card asks is answered by the button. */
async function saveRate(tabId: string, loadId: string, loadNumber: string, amount: number): Promise<StepResult> {
    const outcome = await approveWrite(tabId, 'tms_loads.edit', { load_id: loadId, original_rate_usd: amount }, { approvedInDrawer: true })
    const refusal = refusalOf(outcome)
    if (refusal) return { ok: false, message: refusal }
    return { ok: true, row: await after(loadNumber) }
}

/**
 * Creates the invoice (`tms_invoices.create`). One load per call; the drawer's button approved it, so a question (the gateway's
 * per-minute budget of unasked loads ran out) is answered by it rather than shown as a refusal.
 */
async function createDraft(tabId: string, loadNumber: string, params: CreateInvoiceParams): Promise<StepResult> {
    const outcome = await approveWrite(tabId, 'tms_invoices.create', params, { approvedInDrawer: true })
    const refusal = refusalOf(outcome)
    if (refusal) return { ok: false, message: refusal, row: await after(loadNumber) }
    return { ok: true, row: await after(loadNumber) }
}

/**
 * Create invoice: the rate first when the load had none (`rate` is the amount typed), then the draft. Stops at the first step
 * that is not done, and says why. Nothing is sent to the broker.
 */
export async function approveAndCreate(args: {
    tabId: string
    loadId: string
    loadNumber: string
    params: CreateInvoiceParams
    rate?: number
}): Promise<StepResult> {
    const { tabId, loadId, loadNumber, params, rate } = args
    if (rate !== undefined) {
        const saved = await saveRate(tabId, loadId, loadNumber, rate)
        if (!saved.ok) return saved
    }
    return createDraft(tabId, loadNumber, params)
}

/**
 * Confirm skip & submit (Needs review only): the POD is accepted with the user's reason, then the draft is created. The reason is the
 * accept's; the issue stays in the audit record. A failed accept stops before anything is created.
 */
export async function skipAndCreate(args: {
    tabId: string
    loadId: string
    loadNumber: string
    reason: string
    params: CreateInvoiceParams
}): Promise<StepResult> {
    const { tabId, loadId, loadNumber, reason, params } = args
    const accepted = await confirmDocumentMatch(tabId, loadId, loadNumber, reason)
    if (!accepted.ok) return accepted
    return createDraft(tabId, loadNumber, params)
}
